import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { overallTypeLabel } from '../../../ui/TopSection';
import { useAnalysisStore, FoodDrinksFilter } from '../../../../stores/appAnalysisStore';
import { cn } from '../../../../lib/utils';
import {
  borderGrey,
  cardBg,
  cardClass,
  darkOrangeTextColor,
  elevatedBg,
  greenTextColor,
  placeholderPrimary,
  primaryText,
  secondaryText,
  semiBoldText,
  tertiaryText,
} from '../../../../styling/stylingHelper';

/** Badge colours: orange for waste, green for savings. */
export const WASTE_BADGE_CLASS = cn('bg-amber-50 dark:bg-amber-900/20', darkOrangeTextColor);
export const SAVINGS_BADGE_CLASS = cn('bg-green-50 dark:bg-green-900/20', greenTextColor);

const FILTER_OPTIONS: FoodDrinksFilter[] = ['all', 'food', 'drinks'];

/** Global All / Food / Drinks segmented control, kept in appAnalysisStore across the three pages. */
export const FoodDrinksToggle: React.FC = () => {
  const { t } = useTranslation();
  const value = useAnalysisStore(s => s.foodDrinksFilter);
  const setValue = useAnalysisStore(s => s.setFoodDrinksFilter);
  return (
    <div className={cn('inline-flex rounded-lg border p-0.5', borderGrey, elevatedBg)} role="radiogroup" aria-label={t('analysis.filter.label')}>
      {FILTER_OPTIONS.map(option => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          onClick={() => setValue(option)}
          className={cn(
            'px-3 py-1.5 text-sm rounded-md transition-colors',
            value === option
              ? cn(cardBg, primaryText, semiBoldText, 'shadow-sm')
              : cn(secondaryText, 'hover:text-textPrimary dark:hover:text-textPrimary-dark')
          )}
        >
          {overallTypeLabel(t, option)}
        </button>
      ))}
    </div>
  );
};

/** True when `side` passes the global food/drinks filter. */
export const matchesFoodDrinks = (filter: FoodDrinksFilter, side: 'food' | 'drinks') =>
  filter === 'all' || filter === side;

interface DetailHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  totalLabel: string;
  totalValue: string;
  totalClassName: string;
  showWindow?: boolean;
}

/** Detail page header: back link, title + icon, formula subtitle, fixed window label, filter, headline total. */
export const AnalysisDetailHeader: React.FC<DetailHeaderProps> = ({ icon, title, subtitle, totalLabel, totalValue, totalClassName, showWindow = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => navigate('/analysis')}
        className={cn('inline-flex items-center gap-1 text-sm hover:underline', secondaryText)}
      >
        <ArrowLeft className="w-4 h-4" />
        {t('analysis.backToAnalysis')}
      </button>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <span className={cn('flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0', elevatedBg)}>{icon}</span>
          <div className="min-w-0">
            <h1 className={cn('text-xl lg:text-2xl', semiBoldText, primaryText)}>{title}</h1>
            <p className={cn('text-sm mt-0.5', secondaryText)}>{subtitle}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showWindow && <span className={cn('px-2.5 py-1 rounded-md border text-xs', borderGrey, secondaryText)}>{t('analysis.last60Days')}</span>}
          <FoodDrinksToggle />
        </div>
      </div>
      <div className={cn(cardClass, 'p-4 flex items-baseline justify-between gap-3')}>
        <span className={cn('text-sm', secondaryText)}>{totalLabel}</span>
        <span className={cn('text-xl lg:text-2xl font-bold', totalClassName)}>{totalValue}</span>
      </div>
    </div>
  );
};

/** Search input for the overview lists (product / group / supplier). */
export const AnalysisSearch: React.FC<{ value: string; onChange: (v: string) => void; placeholder: string }> = ({ value, onChange, placeholder }) => (
  <div className={cn('flex items-center gap-2 rounded-lg border px-3 py-2', borderGrey, cardBg)}>
    <Search className={cn('w-4 h-4 flex-shrink-0', tertiaryText)} />
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn('w-full bg-transparent dark:bg-transparent text-sm outline-none', primaryText, placeholderPrimary)}
    />
  </div>
);

interface RankedGroupRowProps {
  rank: number;
  title: string;
  meta: string;
  badge: string;
  badgeClassName: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  children?: React.ReactNode;
}

/** Collapsible ranked group row with a right-aligned € badge; `children` is the expanded body. */
export const RankedGroupRow: React.FC<RankedGroupRowProps> = ({ rank, title, meta, badge, badgeClassName, expanded, onToggleExpanded, children }) => (
  <div className={cn(cardClass, 'border', borderGrey)}>
    <div
      role="button"
      tabIndex={0}
      onClick={onToggleExpanded}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleExpanded(); } }}
      className="flex items-center gap-3 p-3 lg:p-4 cursor-pointer"
    >
      {expanded
        ? <ChevronDown className={cn('w-4 h-4 flex-shrink-0', tertiaryText)} />
        : <ChevronRight className={cn('w-4 h-4 flex-shrink-0', tertiaryText)} />}
      <span className={cn('w-6 text-sm flex-shrink-0', tertiaryText)}>#{rank}</span>
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm truncate', semiBoldText, primaryText)}>{title}</p>
        <p className={cn('text-xs truncate', secondaryText)}>{meta}</p>
      </div>
      <span className={cn('px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap', badgeClassName)}>{badge}</span>
    </div>
    {expanded && <div className={cn('border-t px-3 pb-3 lg:px-4 lg:pb-4', borderGrey)}>{children}</div>}
  </div>
);
